import { useEffect, useState } from 'react';
import { Button } from '@/shared/ui/Button';
import { useDialogos } from '@/shared/ui/DialogosProvider';
import { httpClient } from '@/shared/api/httpClient';
import { licencaApi, type EstadoLicenca } from './licencaApi';

type Complemento = {
  codigo: string;
  nome: string;
  descricao: string | null;
  precoBRL: number;
  precoOriginalBRL: number | null;
  frequenciaMeses: number;
  ativo: boolean;
  validaAte: string | null;
  origem: 'manual' | 'compra' | 'plano';
};

// Página "Minha assinatura" — mostra ao usuário o plano vigente, validade
// e estado da renovação automática. Permite pausar a renovação (cancela o
// preapproval no Mercado Pago). O ciclo já pago segue até vencer; depois
// disso o app volta a exigir nova assinatura.

type Props = {
  estado: EstadoLicenca | null;
  recheck: () => Promise<void> | void;
  carregando: boolean;
};

const FMT_DATA = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

function rotuloStatus(s: EstadoLicenca['status']): { texto: string; cor: string } {
  switch (s) {
    case 'ativa':
      return { texto: 'Ativa', cor: 'var(--color-success)' };
    case 'trial':
      return { texto: 'Período de avaliação', cor: 'var(--color-primary)' };
    case 'vencida':
      return { texto: 'Vencida', cor: 'var(--color-warning)' };
    case 'bloqueada':
      return { texto: 'Bloqueada', cor: 'var(--color-danger)' };
    case 'sem-acesso':
      return { texto: 'Sem acesso', cor: 'var(--color-danger)' };
    default:
      return { texto: 'Desconhecida', cor: 'var(--color-text-muted)' };
  }
}

function rotuloPlano(codigo: string | null): string {
  if (!codigo) return '—';
  if (codigo === 'trial') return 'Trial (avaliação)';
  if (codigo === 'mensal') return 'Mensal';
  if (codigo === 'trimestral') return 'Trimestral';
  if (codigo === 'anual') return 'Anual';
  // Planos custom criados pelo admin caem aqui — capitaliza o código.
  return codigo.charAt(0).toUpperCase() + codigo.slice(1);
}

export function MinhaLicencaPage({ estado, recheck, carregando }: Props) {
  const [pausando, setPausando] = useState(false);
  const [verificando, setVerificando] = useState(false);
  const [feedback, setFeedback] = useState<
    { tipo: 'ok' | 'erro'; texto: string } | null
  >(null);
  const dialogos = useDialogos();

  if (!estado) {
    return (
      <div style={{ padding: 24, color: 'var(--color-text-muted)' }}>Carregando licença…</div>
    );
  }

  const status = rotuloStatus(estado.status);
  const validade = estado.validaAte
    ? FMT_DATA.format(new Date(estado.validaAte))
    : '—';
  const podePausar = estado.renovacaoAutomatica;

  async function pausar() {
    const ok = await dialogos.confirmar({
      titulo: 'Pausar renovação automática',
      mensagem:
        'A cobrança recorrente no Mercado Pago será cancelada. ' +
        'O período já pago segue válido até vencer. ' +
        'Depois disso, para continuar usando o sistema você precisará assinar de novo. ' +
        'Deseja continuar?',
      textoConfirmar: 'Pausar renovação',
    });
    if (!ok) return;
    setPausando(true);
    setFeedback(null);
    try {
      const r = await licencaApi.pausarRenovacao();
      if ('ok' in r && r.ok) {
        setFeedback({
          tipo: 'ok',
          texto: r.cancelouNoMP
            ? 'Renovação cancelada no Mercado Pago.'
            : 'Renovação automática desligada (não havia preapproval ativo no MP).',
        });
        await recheck();
      } else {
        setFeedback({
          tipo: 'erro',
          texto: 'erro' in r ? r.erro : 'Falha desconhecida.',
        });
      }
    } catch (err) {
      setFeedback({
        tipo: 'erro',
        texto: err instanceof Error ? err.message : 'Falha ao pausar renovação.',
      });
    } finally {
      setPausando(false);
    }
  }

  async function verificarNoMP() {
    setVerificando(true);
    setFeedback(null);
    try {
      const r = await licencaApi.verificarMP();
      if ('erro' in r) {
        setFeedback({ tipo: 'erro', texto: r.erro });
        return;
      }
      if (r.statusMP === null) {
        setFeedback({
          tipo: 'ok',
          texto: 'Nenhuma assinatura recorrente vinculada a este e-mail no Mercado Pago.',
        });
      } else {
        setFeedback({
          tipo: 'ok',
          texto: r.ativaNoMP
            ? `Mercado Pago: renovação automática ATIVA (status "${r.statusMP}").`
            : `Mercado Pago: renovação NÃO está ativa (status "${r.statusMP}"). Estado sincronizado.`,
        });
      }
      await recheck();
    } catch (err) {
      setFeedback({
        tipo: 'erro',
        texto: err instanceof Error ? err.message : 'Falha ao verificar no Mercado Pago.',
      });
    } finally {
      setVerificando(false);
    }
  }

  return (
    <div style={{ padding: 24, maxWidth: 720 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 20,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h2 style={{ margin: '0 0 8px', fontSize: 22, color: 'var(--color-text)' }}>
            Minha assinatura
          </h2>
          <p style={{ margin: 0, color: 'var(--color-text-muted)', fontSize: 14 }}>
            Plano vigente desta máquina e estado da renovação automática.
          </p>
        </div>
        <Button
          variante="ghost"
          onClick={() => recheck()}
          disabled={carregando || pausando || verificando}
          title="Consulta o servidor agora — útil logo após pagar ou se a renovação não apareceu como ativa."
        >
          {carregando ? 'Verificando…' : 'Atualizar agora'}
        </Button>
      </div>

      <div
        style={{
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 12,
          padding: 20,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 16,
        }}
      >
        <Campo rotulo="Plano">{rotuloPlano(estado.plano)}</Campo>
        <Campo rotulo="Status">
          <span style={{ color: status.cor, fontWeight: 600 }}>{status.texto}</span>
        </Campo>
        <Campo rotulo="Válida até">{validade}</Campo>
        <Campo rotulo="Renovação automática">
          {estado.renovacaoAutomatica ? (
            <span style={{ color: 'var(--color-success)', fontWeight: 600 }}>Ativa</span>
          ) : (
            <span style={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>Desligada</span>
          )}
        </Campo>
        {estado.email && <Campo rotulo="E-mail vinculado">{estado.email}</Campo>}
      </div>

      <div
        style={{
          marginTop: 20,
          padding: 16,
          background: 'var(--color-surface-muted)',
          border: '1px solid var(--color-border)',
          borderRadius: 12,
        }}
      >
        <h3 style={{ margin: '0 0 8px', fontSize: 15, color: 'var(--color-text)' }}>
          Renovação automática
        </h3>
        {podePausar ? (
          <>
            <p style={{ margin: '0 0 12px', color: 'var(--color-text-muted)', fontSize: 13, lineHeight: 1.5 }}>
              Sua assinatura é renovada automaticamente pelo Mercado Pago no
              fim de cada ciclo. Você pode pausar agora — o ciclo já pago
              continua válido até <strong>{validade}</strong> e nenhuma nova
              cobrança será feita.
            </p>
            <Button onClick={pausar} disabled={pausando || carregando || verificando}>
              {pausando ? 'Pausando…' : 'Pausar renovação automática'}
            </Button>
          </>
        ) : (
          <p style={{ margin: 0, color: 'var(--color-text-muted)', fontSize: 13, lineHeight: 1.5 }}>
            Esta máquina não tem renovação automática ativa. Quando a licença
            vencer, você precisará assinar manualmente para continuar usando
            o sistema.
          </p>
        )}
        <div style={{ marginTop: 12 }}>
          <Button
            variante="ghost"
            onClick={verificarNoMP}
            disabled={verificando || pausando || carregando}
            title="Consulta diretamente o Mercado Pago se a renovação automática está ativa e sincroniza esta tela."
          >
            {verificando ? 'Verificando…' : 'Verificar no Mercado Pago'}
          </Button>
        </div>
        {feedback && (
          <div
            role={feedback.tipo === 'erro' ? 'alert' : 'status'}
            style={{
              marginTop: 12,
              padding: '8px 12px',
              borderRadius: 6,
              fontSize: 13,
              background:
                feedback.tipo === 'ok'
                  ? 'var(--color-success-soft)'
                  : 'var(--color-danger-soft)',
              color: feedback.tipo === 'ok' ? 'var(--color-success)' : 'var(--color-danger)',
              border:
                feedback.tipo === 'ok'
                  ? '1px solid var(--color-success)'
                  : '1px solid var(--color-danger)',
            }}
          >
            {feedback.texto}
          </div>
        )}
      </div>

      <SecaoComplementos />
    </div>
  );
}

const FMT_MOEDA = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

function SecaoComplementos() {
  const [itens, setItens] = useState<Complemento[] | null>(null);

  useEffect(() => {
    httpClient
      .get<{ produtos: Complemento[] }>('/api/complementos')
      .then((r) => setItens(r.produtos))
      .catch(() => setItens([]));
  }, []);

  if (!itens || itens.length === 0) return null;

  return (
    <div style={{ marginTop: 28 }}>
      <h3 style={{ margin: '0 0 4px' }}>Complementos</h3>
      <p style={{ color: 'var(--color-text-muted)', fontSize: 13, marginTop: 0 }}>
        Recursos pagos à parte da assinatura. A compra é feita no site; após pagar, o recurso é
        liberado automaticamente nesta conta.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {itens.map((p) => {
          const freq = p.frequenciaMeses === 1 ? '/mês' : ` a cada ${p.frequenciaMeses} meses`;
          return (
            <div
              key={p.codigo}
              className="card"
              style={{ margin: 0, padding: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}
            >
              <div style={{ minWidth: 0 }}>
                <strong>{p.nome}</strong>
                <div style={{ fontSize: 13, color: 'var(--color-text-muted)', marginTop: 2 }}>
                  {p.ativo
                    ? p.origem === 'plano'
                      ? 'Ativo — incluso no seu plano'
                      : `Ativo${p.validaAte ? ` até ${FMT_DATA.format(new Date(p.validaAte))}` : ''}`
                    : `${FMT_MOEDA.format(p.precoBRL)}${freq}`}
                </div>
              </div>
              {p.ativo ? (
                <span style={{ color: 'var(--color-success)', fontWeight: 600, whiteSpace: 'nowrap' }}>● Ativo</span>
              ) : (
                <a
                  className="btn btn--primary"
                  href={`https://oficinaagil.softjack.com.br/assinar/produto/${encodeURIComponent(p.codigo)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  Assinar no site
                </a>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Campo({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--color-text-muted)', fontWeight: 600, marginBottom: 4 }}>
        {rotulo.toUpperCase()}
      </div>
      <div style={{ fontSize: 14, color: 'var(--color-text)' }}>{children}</div>
    </div>
  );
}
